import posthog from 'posthog-js';
import type { User } from '~/types';
import { isOtherIdentifiedUser, redactCapture } from '~/utils/analytics/posthogPrivacy';

// Product analytics: page views, tool opens and sign-ins per agent, queryable in PostHog.
// Off unless NUXT_PUBLIC_POSTHOG_KEY is set. Staff "view as" sessions are not captured, so an
// admin browsing as an agent never shows up as that agent's activity.
const personProperties = (user: User) => ({
  email: user.email,
  name: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email,
  role: user.role,
  market: user.agentProfile?.market ?? null,
});

export default defineNuxtPlugin(() => {
  const {
    public: { posthog: config },
  } = useRuntimeConfig();

  if (!config.key) {
    return { provide: { posthog: null } };
  }

  posthog.init(config.key, {
    api_host: config.host,
    // Sent from the router below so client-side navigations count as page views too.
    capture_pageview: false,
    capture_pageleave: true,
    person_profiles: 'identified_only',
    disable_session_recording: true,
    // Sign-in links carry a login token in the URL: it never leaves the browser.
    before_send: (capture) => (capture ? redactCapture(capture) : capture),
  });

  const authStore = useAuthStore();
  const router = useRouter();
  let identifiedUserId: string | null = null;

  watch(
    () => [authStore.user?.id ?? null, authStore.getIsImpersonating] as const,
    ([userId, isImpersonating]) => {
      if (isImpersonating) {
        posthog.opt_out_capturing();
        return;
      }
      if (posthog.has_opted_out_capturing()) posthog.opt_in_capturing();

      if (userId && authStore.user) {
        // Another account still identified in this browser (signed in over it without signing out).
        if (isOtherIdentifiedUser(userId, posthog.get_distinct_id(), posthog.get_property('$user_state'))) {
          posthog.reset();
        }
        posthog.identify(userId, personProperties(authStore.user));
        identifiedUserId = userId;
        return;
      }
      // Only a real sign-out resets; a guest keeps one anonymous id across visits.
      if (identifiedUserId) {
        posthog.reset();
        identifiedUserId = null;
      }
    },
    { immediate: true },
  );

  const capturePageview = () => posthog.capture('$pageview', { $current_url: window.location.href });

  onNuxtReady(capturePageview);
  router.afterEach((to, from) => {
    if (to.fullPath !== from.fullPath) nextTick(capturePageview);
  });

  return { provide: { posthog } };
});
